import { create } from "zustand";
import { getTimezoneAbbr } from "../utils/timezoneFormatter.js";

const KNOWN_OFFSET_ZONES = {
    "-720": "Pacific/Auckland",
    "-660": "Pacific/Noumea",
    "-600": "Australia/Sydney",
    "-570": "Australia/Adelaide",
    "-540": "Asia/Tokyo",
    "-480": "Asia/Singapore",
    "-420": "Asia/Bangkok",
    "-390": "Asia/Yangon",
    "-360": "Asia/Dhaka",
    "-345": "Asia/Kathmandu",
    "-330": "Asia/Kolkata",
    "-300": "Asia/Karachi",
    "-270": "Asia/Kabul",
    "-240": "Asia/Dubai",
    "-210": "Asia/Tehran",
    "-180": "Europe/Moscow",
    "-120": "Europe/Athens",
    "-60": "Europe/Paris",
    "0": "UTC",
    "60": "Atlantic/Cape_Verde",
    "120": "America/Noronha",
    "180": "America/Sao_Paulo",
    "210": "America/St_Johns",
    "240": "America/New_York",
    "300": "America/Chicago",
    "360": "America/Denver",
    "420": "America/Los_Angeles",
    "480": "America/Anchorage",
    "540": "America/Adak",
    "600": "Pacific/Honolulu",
};

/**
 * Accurately resolves browser timezone using both Intl and dynamic offset.
 * Resolves cached timezone mismatch issues (e.g. Windows timezone changed while Chrome open).
 * @returns {string} IANA timezone identifier
 */
export const getBrowserTimezone = () => {
    try {
        const offsetMinutes = new Date().getTimezoneOffset();
        const intlTz = typeof Intl !== "undefined" ? Intl.DateTimeFormat().resolvedOptions().timeZone : null;

        // If intlTz is present, verify that its current offset matches the system's actual getTimezoneOffset()
        if (intlTz && typeof intlTz === "string" && intlTz.trim() !== "") {
            const trimmed = intlTz.trim();
            try {
                const now = new Date();
                const parts = new Intl.DateTimeFormat("en-US", {
                    timeZone: trimmed,
                    year: "numeric",
                    month: "numeric",
                    day: "numeric",
                    hour: "numeric",
                    minute: "numeric",
                    second: "numeric",
                    hourCycle: "h23",
                }).formatToParts(now).reduce((acc, p) => { acc[p.type] = p.value; return acc; }, {});
                const asUtc = Date.UTC(parts.year, parts.month - 1, parts.day, parts.hour, parts.minute, parts.second);
                const intlOffset = Math.round((now.getTime() - asUtc) / 60000);
                if (intlOffset === offsetMinutes) {
                    return trimmed;
                }
            } catch {
                // If checking fails, proceed to offset mapping
            }
        }

        // If intl was stale or mismatched with OS clock offset (e.g. changed Windows timezone while browser open)
        const offsetKey = String(offsetMinutes);
        if (KNOWN_OFFSET_ZONES[offsetKey]) {
            return KNOWN_OFFSET_ZONES[offsetKey];
        }

        const hours = -offsetMinutes / 60;
        if (Number.isInteger(hours)) {
            if (hours >= 0) return `Etc/GMT-${hours}`;
            return `Etc/GMT+${Math.abs(hours)}`;
        }

        return intlTz || "UTC";
    } catch {
        return "UTC";
    }
};

const getInitialMode = () => {
    try {
        if (typeof window !== "undefined" && window.localStorage) {
            const saved = window.localStorage.getItem("analytics_tz_mode");
            if (saved && ["local", "utc", "custom"].includes(saved)) {
                return saved;
            }
        }
    } catch {}
    return "local"; // Default directly to user's PC timezone
};

const getInitialCustomTz = () => {
    try {
        if (typeof window !== "undefined" && window.localStorage) {
            return window.localStorage.getItem("analytics_custom_tz") || "";
        }
    } catch {}
    return "";
};

export const useTimezoneStore = create((set, get) => ({
    mode: getInitialMode(), // "event" | "local" | "utc" | "utc+2" | "custom"
    customTz: getInitialCustomTz(),
    browserTimezone: getBrowserTimezone(),

    setMode: (mode) => {
        try {
            if (typeof window !== "undefined" && window.localStorage) {
                window.localStorage.setItem("analytics_tz_mode", mode);
            }
        } catch {}
        set({ mode });
    },

    setCustomTz: (customTz) => {
        try {
            if (typeof window !== "undefined" && window.localStorage) {
                window.localStorage.setItem("analytics_tz_mode", "custom");
                window.localStorage.setItem("analytics_custom_tz", customTz);
            }
        } catch {}
        set({ mode: "custom", customTz });
    },

    /**
     * Resolves the active IANA timezone string.
     * Directly checks and returns the user's PC timezone by default.
     * @param {string} [eventTimezone="Asia/Kolkata"]
     * @returns {string}
     */
    getActiveTimezone: (eventTimezone = "Asia/Kolkata") => {
        const { mode, customTz } = get();
        if (mode === "utc") return "UTC";
        if (mode === "event") return eventTimezone || "Asia/Kolkata";
        if (mode === "custom" && customTz) return customTz;
        // Default directly checks user's PC timezone
        return getBrowserTimezone() || "UTC";
    },

    refreshBrowserTimezone: () => set({ browserTimezone: getBrowserTimezone() }),

    /**
     * Returns the abbreviation for the active timezone (e.g. GMT+5:30, GMT+2, EDT, UTC)
     * @param {string} [eventTimezone="Asia/Kolkata"]
     * @param {Date} [date]
     * @returns {string}
     */
    getActiveAbbr: (eventTimezone = "Asia/Kolkata", date = new Date()) => {
        const activeTz = get().getActiveTimezone(eventTimezone);
        if (activeTz === "UTC") return "UTC";
        if (activeTz === "Etc/GMT-2") return "GMT+2";
        if (activeTz === "Etc/GMT-3") return "GMT+3";
        return getTimezoneAbbr(activeTz, date);
    },
}));

if (typeof window !== "undefined") {
    const sync = () => {
        try {
            const currentTz = getBrowserTimezone();
            const state = useTimezoneStore.getState();
            if (currentTz && currentTz !== state.browserTimezone) {
                useTimezoneStore.setState({ browserTimezone: currentTz });
            }
        } catch {}
    };
    window.addEventListener("focus", sync);
    window.addEventListener("visibilitychange", () => {
        if (!document.hidden) sync();
    });
    setInterval(sync, 2000);
}

